import {isSavingAllowed} from '@gaia/hooks/useAreas';
import useIsSaved from '@gaia/hooks/useIsSaved';
import type {TrackDispatcher} from '@gaia/hooks/useTracks';
import {useUser} from '@gaia/stores/user';
import type {ImportState, SharedData} from '@gaia/types';
import saveWaypoint, {getIsWaypointModifiedSinceSave} from '@gaia/utils/gaia/saveWaypoint';
import map from 'lodash/map';
import merge from 'lodash/merge';
import {useCallback, useEffect, useMemo, useState} from 'react';

import archive from './dispatchers/archive';
import bulkArchive from './dispatchers/bulkArchive';
import bulkUpdate from './dispatchers/bulkUpdate';
import create from './dispatchers/create';
import takeLocal from './dispatchers/takeLocal';
import update from './dispatchers/update';
import updateProperties from './dispatchers/updateProperties';

export type WaypointDispatcher = (action: WaypointAction) => string[];

// eslint-disable-next-line max-params
export function useWaypoints(
    tracks: Record<string, Track>,
    trackDispatcher: TrackDispatcher,
    interactionType: InteractionState['type'],
    sharedData: SharedData,
    importState: ImportState
): [{[localId: string]: Waypoint | TrackPoint}, WaypointDispatcher, number] {
    const {isAuthenticated} = useUser();

    const [waypoints, setWaypoints] = useState<{[localId: string]: Waypoint}>({});

    const [unsaved, addSavePromise] = useIsSaved();

    const trackPoints: {[id: string]: Waypoint} = useMemo(
        () =>
            merge(
                {},
                ...map(tracks, (track) => {
                    return track.isGeometryLoaded && track.points;
                })
            ),
        [tracks]
    );

    const dispatcher = useCallback(
        (action: WaypointAction): string[] => {
            if (action.type === 'create') return create(action, setWaypoints);
            else if (action.type === 'update')
                return update(action, setWaypoints, trackPoints, trackDispatcher);
            else if (action.type === 'takeLocal')
                return takeLocal(
                    action,
                    waypoints,
                    setWaypoints,
                    trackPoints,
                    trackDispatcher,
                    sharedData
                );
            else if (action.type === 'updateProperties')
                return updateProperties(action, setWaypoints, trackPoints, trackDispatcher);
            else if (action.type === 'archive')
                return archive(action, setWaypoints, trackPoints, trackDispatcher);
            else if (action.type === 'bulkUpdate') return bulkUpdate(action, setWaypoints);
            else if (action.type === 'bulkArchive') return bulkArchive(action, setWaypoints);
            else throw new Error('Unknown action type');
        },
        [trackDispatcher, trackPoints, waypoints, sharedData]
    );

    // When a waypoint has been updated locally, save those changes back to the server.
    useEffect(() => {
        if (isSavingAllowed(interactionType, importState, isAuthenticated)) {
            for (const localId in waypoints) {
                const waypoint = waypoints[localId];

                // If a new folder has been created while importing,
                // file all imported waypoints to it.
                if (
                    importState.status === 'saving' &&
                    importState.folder &&
                    waypoint.properties.isLatestImport &&
                    !waypoint.properties.parent_folder_id
                ) {
                    waypoint.properties.parent_folder_id = importState.folder.id;
                }

                if (getIsWaypointModifiedSinceSave(waypoint)) {
                    addSavePromise(
                        saveWaypoint(waypoint, (id) =>
                            dispatcher({
                                type: 'updateProperties',
                                properties: {localId, id},
                            })
                        )
                    );
                }
            }
        }
    }, [
        addSavePromise,
        dispatcher,
        importState,
        interactionType,
        isAuthenticated,
        tracks,
        waypoints,
    ]);

    return [
        useMemo(() => ({...waypoints, ...trackPoints}), [waypoints, trackPoints]),
        dispatcher,
        unsaved,
    ];
}
